import { Button, DataTable, DefinitionList, FilterToolbar, Modal, notify, PageHeader, SearchInput, Select, StatusBadge, statusOptions, TextAreaField, Truncate, useDebouncedText, useUrlFilters, type Column } from "@flexfreight/ui";
import { keepPreviousData, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { api, ApiError } from "../../api/client";
import { useConsoleMe } from "../../auth/context";
import { useCursorPages } from "./cursorPages";
import { ZohoFailuresNotice } from "../freight/ZohoFailures";

type Report = { id: string; source: "portal" | "console"; page: string; description: string; userAgent: string | null; viewport: string | null; status: "new" | "triaged" | "fixed" | "wont_fix"; staffNote: string | null; reporter: string; reporterEmail: string | null; orgId: string | null; orgName: string | null; createdAt: string };
type ReportsPage = { reports: Report[]; counts: { new: number; open: number }; next: string | null };
const STATUS_OPTIONS = statusOptions("problem").map(({ value, label }) => ({ value: value as Report["status"], label }));
const when = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
const where = (r: Report) => (r.source === "portal" ? "Customer portal" : "Console");
/** The list opens on what still needs a look; "open" is the default view, not a narrowing filter. */
const FILTERS = { status: "open", q: "" };

/** Admin Center > Problem reports (A.41): "Report a problem" from the portal and the console. */
export function ProblemsPage() {
  const me = useConsoleMe();
  const f = useUrlFilters(FILTERS, useSearchParams());
  const v = f.values;
  const [search, setSearch] = useDebouncedText(v.q, (q) => f.set({ q }), 300);
  const [open, setOpen] = useState<Report | null>(null);
  const list = useInfiniteQuery({
    queryKey: ["console", "bugs", v.status, v.q],
    initialPageParam: "",
    queryFn: ({ pageParam }) => api<ReportsPage>(`/v1/console/bug-reports?${new URLSearchParams({ status: v.status, q: v.q, ...(pageParam ? { before: pageParam } : {}) })}`),
    getNextPageParam: (l) => l.next ?? undefined,
    placeholderData: keepPreviousData,
  });
  const pages = useCursorPages(list, (p) => p.reports, `${v.status}|${v.q}`);
  const counts = list.data?.pages[0]?.counts;
  const columns: Column<Report>[] = [
    {
      key: "what",
      header: "What happened",
      primary: true,
      truncate: "custom",
      render: (r) => (
        <Truncate text={r.description}>
          <button type="button" className="ffx-dt__open" onClick={() => setOpen(r)}>
            {r.description}
          </button>
        </Truncate>
      ),
    },
    { key: "reported", header: "Reported", width: "10rem", render: (r) => <time dateTime={r.createdAt}>{when.format(new Date(r.createdAt))}</time>, sub: where },
    {
      key: "by",
      header: "By",
      width: "12rem",
      truncate: true,
      text: (r) => r.reporter,
      render: (r) => r.reporter,
      sub: (r) => (r.orgName && r.orgId ? <Link to={`/customers/${r.orgId}`}>{r.orgName}</Link> : null),
      subText: (r) => r.orgName ?? "",
    },
    { key: "page", header: "Page", width: "12rem", mobile: "hide", truncate: true, text: (r) => r.page, render: (r) => <span className="ffx-num ffa-small">{r.page}</span> },
    { key: "status", header: "Status", width: "8rem", render: (r) => <StatusBadge domain="problem" value={r.status} size="sm" /> },
  ];
  return (
    <section className="ffa-page">
      <PageHeader eyebrow="Admin Center" title="Problem reports" subtitle='What customers and staff reported with "Report a problem", with the page they were on.' meta={counts ? <span><strong>{counts.new}</strong> new · <strong>{counts.open}</strong> open</span> : null} />
      {me.capabilities.includes("billing:read") ? <ZohoFailuresNotice /> : null}
      <FilterToolbar
        label="Report filters"
        search={<SearchInput label="Search reports, pages, people or customers" value={search} onChange={setSearch} />}
        items={[
          {
            key: "status",
            priority: 1,
            active: v.status !== "open",
            inline: <Select variant="toolbar" label="Status" value={v.status} active={v.status !== "open"} onChange={(status) => f.set({ status })} options={[{ value: "open", label: "Open" }, ...STATUS_OPTIONS, { value: "all", label: "All" }]} />,
          },
        ]}
        anyActive={f.anyActive}
        showReset
        onReset={() => {
          setSearch("");
          f.reset();
        }}
      />
      <DataTable
        caption="Problem reports"
        columns={columns}
        rows={pages.rows}
        rowKey={(r) => r.id}
        loading={pages.loading || list.isFetching}
        error={list.isError ? { title: "Couldn't load problem reports", message: message(list.error), onRetry: () => void list.refetch() } : null}
        empty="No reports here."
        onRowClick={setOpen}
        pagination={pages.pagination}
      />
      {open ? <ReportDialog report={open} onClose={() => setOpen(null)} /> : null}
    </section>
  );
}

function ReportDialog({ report, onClose }: { report: Report; onClose: () => void }) {
  const me = useConsoleMe();
  const qc = useQueryClient();
  const canEdit = me.capabilities.includes("settings:manage");
  const [status, setStatus] = useState(report.status);
  const [note, setNote] = useState(report.staffNote ?? "");
  const save = useMutation({
    mutationFn: () => api(`/v1/console/bug-reports/${report.id}`, { method: "PATCH", body: { status, staffNote: note.trim() || null } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["console", "bugs"] });
      notify.success("Saved");
      onClose();
    },
    onError: (e) => notify.error("Not saved", message(e)),
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={`Reported by ${report.reporter}`}
      description={`${when.format(new Date(report.createdAt))} on ${report.source === "portal" ? "the customer portal" : "the console"}${report.orgName ? ` · ${report.orgName}` : ""}`}
      footer={
        canEdit ? (
          <>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
          </>
        ) : (
          <Button variant="primary" onClick={onClose}>Close</Button>
        )
      }
    >
      <div className="ffa-form">
        <p className="ffa-msg__body">{report.description}</p>
        <DefinitionList
          items={[
            { term: "Page", detail: <span className="ffx-num">{report.page}</span> },
            { term: "Screen", detail: report.viewport ?? "-" },
            { term: "Browser", detail: report.userAgent ?? "-" },
            report.reporterEmail ? { term: "Email", detail: report.reporterEmail } : null,
            !canEdit && report.staffNote ? { term: "Staff note", detail: report.staffNote } : null,
          ]}
        />
        {canEdit ? (
          <div className="ffx-fields">
            <Select className="ffx-span" label="Status" value={status} onChange={(v) => setStatus(v)} options={STATUS_OPTIONS} />
            <TextAreaField className="ffx-span" label="Staff note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={2000} optional />
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
